import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AUTH_SESSION_HANDLER } from '../auth/auth-session.token';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);
  private refreshing = false;
  private readonly refresh$ = new BehaviorSubject<string | null>(null);
  // HttpBackend bypasses interceptors para evitar bucle infinito en el refresh
  private readonly bare: HttpClient;

  constructor(backend: HttpBackend) {
    this.bare = new HttpClient(backend);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401 || req.url.includes('/auth/refresh-token')) {
          return throwError(() => err);
        }

        if (this.refreshing) {
          return this.refresh$.pipe(
            filter((t) => t !== null),
            take(1),
            switchMap((token) => next.handle(this.withToken(req, token!)))
          );
        }

        this.refreshing = true;
        this.refresh$.next(null);

        return this.bare
          .post<{ accessToken: string }>('/api/auth/refresh-token', {}, { withCredentials: true })
          .pipe(
            tap(({ accessToken }) => {
              this.injector.get(AUTH_SESSION_HANDLER, { optional: true })?.setAccessToken(accessToken);
              this.refresh$.next(accessToken);
              this.refreshing = false;
            }),
            switchMap(({ accessToken }) => next.handle(this.withToken(req, accessToken))),
            catchError((refreshErr) => {
              this.refreshing = false;
              this.injector.get(AUTH_SESSION_HANDLER, { optional: true })?.invalidate();
              return throwError(() => refreshErr);
            })
          );
      })
    );
  }

  private withToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
